import React, { useMemo, useState, useEffect } from "react";
import { get } from "lodash";
import { connect } from "react-redux";
import { getFormValues, getFormInitialValues, getFormSyncErrors, change } from "redux-form";
import { withTranslation } from "react-i18next";
import DynamicForm from "../common/DynamicForm";
import { GET_MAX_FILE_SIZE_FROM_DICT } from "constants/index";
import Loader from "../common/Loader";
import baseService from "services/BaseService";
import { StreetModes } from "constants/index";
import { getModalFormDataFromState } from "helpers";
import { getMergedStaticBlocks } from "./config";

const STATIC_FORM_NAME = "address-modal-form";

const ZIP_CODE_REGEX = "^\\d{5}$"; // only 5 digits

const FALLBACK_OBJECT_TYPE = "CUSTOMER";

const mapStateToProps = (state, ownProps) => {
    const addressFormName = ownProps.customFormName ? ownProps.customFormName : STATIC_FORM_NAME;
    const modalFormData = getModalFormDataFromState(ownProps.staticBlockFormKey, state);
    return {
        addressFormName,
        formErrors: getFormSyncErrors(addressFormName)(state),
        formValues: getFormValues(addressFormName)(state),
        formInitiallValues: getFormInitialValues(addressFormName)(state),
        modalFormData: get(modalFormData, "addressData") || get(modalFormData, "custAddress"),
    };
};

const mapDispatchToProps = { change };

const AddressForm = props => {
    const {
        t,
        address,
        disabled = false,
        customerId,
        customerObjectType,
        formErrors,
        formValues,
        formInitiallValues,
        onValuesUpdate,
        onErrorsUpdate,
        change,
        onStreetModeUpdate,
        modalFormData,
        withoutAddressType,
        withoutStreetMode,
        addressFormName,
        inputKey,
        onBuildingNumbersUpdate
    } = props;

    const objectType = customerObjectType || FALLBACK_OBJECT_TYPE;

    const streetTypes = t("dictionary:streetType", { returnObjects: true });
    const streetTypesOptions = Object.keys(streetTypes).map(key => ({ key, value: streetTypes[key] }));

    const [streetMode, setStreetMode] = useState(StreetModes.AUTO);

    const [addressTypes, setAddressTypes] = useState([]);
    const [isAddressTypesLoading, setIsAddressTypesLoading] = useState(true);

    const [buildingNumbers, setBuildingNumbers] = useState([]);
    const [isBuildingNumbersLoading, setIsBuildingNumbersLoading] = useState(false);

    const [zipCodes, setZipCodes] = useState([]);
    const [isZipCodesLoading, setIsZipCodesLoading] = useState(false);

    // const [isNeedToPreloadOptions, setIsNeedToPreloadOptions] = useState(false);
    const [isOptionsInitialised, setIsOptionsInitialised] = useState(true);

    const [isZipCodeAddressLoading, setIsZipCodeAddressLoading] = useState(false);

    const [possibleAddress, setPossibleAddress] = useState(null);
    const [formRerenderKey, setFormRerenderKey] = useState(null);

    const maxFileSize = useMemo(() => GET_MAX_FILE_SIZE_FROM_DICT("form"), []);

    const addressType = useMemo(() => {
        return formValues && formValues.type;
    }, [formValues]);

    const settlementId = useMemo(() => {
        return formValues && formValues.settlement;
    }, [formValues]);

    const streetId = useMemo(() => {
        return formValues && formValues.street;
    }, [formValues]);

    const buildId = useMemo(() => {
        return get(formValues, 'buildingNumber.value', "") || get(formValues, 'buildingNumber', "");
    }, [formValues]);

    const buildIdCreatableInput = useMemo(() => {
        return formValues && formValues.buildingNumberCreatableInput;
    }, [formValues]);

    const buildingSecondNumber = useMemo(() => {
        return formValues && formValues.buildingSecondNumber;
    }, [formValues]);

    const apartmentNumber = useMemo(() => {
        return formValues && formValues.apartmentNumber;
    });

    const zipCodeCreatableInput = useMemo(() => {
        return formValues && formValues.zipCodeCreatableInput;
    }, [formValues]);

    const description = useMemo(() => {
        return formValues && formValues.description;
    }, [formValues]);

    const initAddressForm = useMemo(() => {
        const widgets = [];
        const typeWidget = {
            key: "type",
            title: t("type"),
            clearable: true,
            savedValue: get(possibleAddress, "addressType", ""),
            disabled: address,
            values: addressTypes.map(item => ({ key: item.code, value: item.name })),
            isReq: true,
            customWidth: "full-width",
            widgetType: "select",
        };
        if (!address && !withoutAddressType) {
            widgets.push(typeWidget);
        }

        // we need to check if the values initialised from adress was changed in order to reser some widgets available options
        const settlementSavedValue = get(possibleAddress, "id", "") || get(address, "settlement.id", "");
        const isSettlementChanged = formInitiallValues && settlementId !== get(formInitiallValues, "settlement", "");
        let settlementValues =
            possibleAddress && possibleAddress.id ? [{ key: possibleAddress.id, value: possibleAddress.fullName }] : [];

        if (!isSettlementChanged && settlementSavedValue) {
            const fullName = get(address, "settlement.fullName", "");
            const name = get(address, "settlement.name", "");
            const cutString = fullName.indexOf("/", 1);
            const tempPartLabel = cutString >= 0 ? fullName.substr(cutString) : "";
            const modifiedLabel = name + tempPartLabel;
            settlementValues = [{ key: settlementSavedValue, value: modifiedLabel }];
        }

        const streetSavedValue = get(possibleAddress, "street[0].id", "") || get(address, "street.id", "");
        const isStreetChanged = formInitiallValues && streetId !== get(formInitiallValues, "street", "");
        let streetValues =
            possibleAddress && possibleAddress.street
                ? possibleAddress.street.map(item => ({ key: item.id, value: item.name }))
                : [];
        if (!isSettlementChanged && !isStreetChanged && streetSavedValue) {
            const streetName = get(address, "street.name", "");
            streetValues = [{ key: streetSavedValue, value: streetName }];
        }

        const isSettlementOrStreetChanged = isSettlementChanged || isStreetChanged;

        const buildingNumber = get(possibleAddress, "buildingNumber", "") || get(address, "buildingNumber", "");
        const buildingNumberOption = buildingNumbers.find(item => item.name === buildingNumber);
        const buildingNumberSavedValue = buildingNumberOption
            ? { value: buildingNumberOption.id, label: buildingNumberOption.name }
            : { value: buildingNumber, label: buildingNumber };
        const isBuildingNumberChanged =
            formInitiallValues && buildId !== get(formInitiallValues, "buildingNumber.value", "");

        let buildingNumberValues = buildingNumbers.map(item => {
            const value = item.ref && item.ref.name && item.ref.id ? `${item.name} ${item.ref.name}` : item.name; // show ref next to the option name
            return { key: item.id, value };
        });
        if (!isSettlementOrStreetChanged && !isBuildingNumberChanged && !buildingNumberOption && buildingNumber) {
            buildingNumberValues = [...buildingNumberValues, { key: buildingNumber, value: buildingNumber }];
        }

        const zipCode = get(possibleAddress, "zipCode", "") || get(address, "zipCode", "");
        const zipCodeOption = zipCodes.find(item => item.zip_code === zipCode);
        const zipCodeSavedValue = zipCodeOption
            ? { value: zipCodeOption.id, label: zipCodeOption.zip_code }
            : { value: zipCode, label: zipCode };
        const isZipCodeChanged =
            formInitiallValues &&
            zipCodeSavedValue.value !==
                (get(formInitiallValues, "zipCode.value", "") || get(formInitiallValues, "zipCode", ""));
        let zipCodeValues = zipCodes.map(item => ({ key: item.id, value: item.zip_code }));

        const duplicateZipCodeValue = zipCodeValues.find(item => item.value === zipCode);

        if (duplicateZipCodeValue && possibleAddress) {
            // filter out fetched zipCode in possibleAddress mode
            zipCodeValues = zipCodeValues.filter(item => item.value !== duplicateZipCodeValue.value && item.key !== duplicateZipCodeValue.key);
        }

        if (
            possibleAddress || (
            !isSettlementOrStreetChanged &&
            !isBuildingNumberChanged &&
            !isZipCodeChanged &&
            !zipCodeOption &&
            zipCode)
        ) {
            zipCodeValues = [...zipCodeValues, { key: zipCode, value: zipCode }];
        }

        const restWidgets = [
            {
                key: "settlement",
                title: t("settlement"),
                clearable: true,
                searchable: true,
                values: settlementValues,
                savedValue: settlementSavedValue,
                isReq: true,
                customAjaxKey: "search_settlement",
                customAjaxMethod: "get",
                customAjaxParams: query => ({ data: { query, page: 1, start: 0, limit: 10 } }),
                customAjaxOptionsConverter: result => {
                    return result.map(item => {
                        const cutString = item.fullName.indexOf("/", 1);
                        const tempPartLabel = cutString >= 0 ? item.fullName.substr(cutString) : "";
                        const modifiedLabel = item.name + tempPartLabel;
                        return { key: item.id, value: modifiedLabel };
                    });
                },
                customWidth: "full-width",
                fieldAjaxOptionsToResetOnChange: "street",
                widgetType: "ajaxCombo",
            },
            {
                key: "street",
                title: t("street"),
                clearable: true,
                isReq: streetMode === StreetModes.AUTO,
                // disabled: isSettlementChanged ? !settlementId : !settlementSavedValue && !settlementId,
                disabled: !settlementId,
                values: streetValues,
                savedValue: streetSavedValue,
                isHidden: streetMode === StreetModes.MANUAL,
                customWidth: "full-width",
                customAjaxKey: "search_street",
                customAjaxMethod: "get",
                customAjaxParams: query => ({ data: { query, settlementId, page: 1, start: 0, limit: 10 } }),
                customAjaxOptionsConverter: result => {
                    return result.map(item => {
                        return { key: item.id, value: item.name };
                    });
                },
                widgetType: "ajaxCombo",
            },

            {
                // key: "streetType",
                key: "streetTypeId",
                title: t("type"),
                clearable: true,
                isReq: streetMode !== StreetModes.AUTO,
                values: streetTypesOptions,
                isHidden: streetMode === StreetModes.AUTO,
                customWidth: "one-third-width",
                withoutExternalConfig: true,
                widgetType: "select",
            },
            {
                // key: "streetName",
                key: "name",
                title: t("name"),
                clearable: true,
                isReq: streetMode !== StreetModes.AUTO,
                maxLength: 255,
                isHidden: streetMode === StreetModes.AUTO,
                customWidth: "two-thirds-width",
                withoutExternalConfig: true,
                widgetType: "input",
            },
            {
                key: "street-mode",
                component: () => {
                    if (streetMode === StreetModes.AUTO) {
                        return (
                            <button onClick={() => handleStreetMode(StreetModes.MANUAL)} class="btn-save" type="button">
                                {t("addNewStreet")}
                            </button>
                        );
                    }
                    return (
                        <button onClick={() => handleStreetMode(StreetModes.AUTO)} class="btn-save" type="button">
                            {t("selectExistingStreet")}
                        </button>
                    );
                },
                customWidth: "full-width",
                withoutExternalConfig: true,
                isHidden: withoutStreetMode,
                widgetType: "customComponent",
            },

            {
                key: "buildingNumber",
                title: t("addressBuildingNumber"),
                searchable: true,
                isReq: true,
                savedValue: buildingNumberSavedValue.value ? buildingNumberSavedValue : null,
                values: buildingNumberValues,
                // rule: "validation.maxLen16$^.{1,16}$", // max 16 characters length
                customWidth: "full-width",
                isCreatable: true,
                hasExValue: true,
                inputProps: { maxLength: 16 }, // limit to 16 characters
                widgetType: "select",
            },
            {
                key: "buildingSecondNumber",
                title: t("addressBuildingSecondNumber"),
                clearable: true,
                isReq: false,
                savedValue:
                    get(possibleAddress, "buildingSecondNumber", "") || get(address, "buildingSecondNumber", ""),
                maxLength: 32,
                errorPlaceholder: true,
                customWidth: "full-width",
                widgetType: "input",
            },
            {
                key: "apartmentNumber",
                title: t("addressApartmentNumber"),
                clearable: true,
                isReq: false,
                savedValue: get(possibleAddress, "apartmentNumber", "") || get(address, "apartmentNumber", ""),
                errorPlaceholder: true,
                maxLength: 32,
                customWidth: "full-width",
                widgetType: "input",
            },
            {
                key: "zipCode",
                title: t("zipCode"),
                clearable: true,
                isReq: true,
                savedValue: zipCodeSavedValue.value ? zipCodeSavedValue : null,
                values: zipCodeValues,
                rule: `validation.zip$${ZIP_CODE_REGEX}`,
                customWidth: "full-width",
                isCreatable: true,
                hasExValue: true,
                inputProps: { maxLength: 5 }, // limit to 5 characters
                widgetType: "select",
            },
            {
                key: "description",
                title: t("addressComment"),
                clearable: true,
                isReq: true,
                savedValue: get(possibleAddress, "description", "") || get(address, "description", ""),
                maxLength: 500,
                autosize: true,
                textAreaClassName: "description",
                customWidth: "full-width",
                widgetType: "multiline",
            },
        ];

        const concatWidgets = widgets.concat(restWidgets);
        let mergedWidgetsWithStaticConfig = modalFormData
            ? getMergedStaticBlocks(t, modalFormData, concatWidgets)
            : concatWidgets;

        const streetWidget = mergedWidgetsWithStaticConfig.find(widget => widget.key === "street");
        const isStreetModeHiddenFromWidget = streetWidget && streetWidget.isHidden || !streetWidget.showAddNewStreet;
        const isManualStreetMode = streetMode === StreetModes.MANUAL; // we should omit check if street mode is already changed (means the button was shown)
        if (!isManualStreetMode && !withoutStreetMode && isStreetModeHiddenFromWidget) {
            mergedWidgetsWithStaticConfig = mergedWidgetsWithStaticConfig.filter(widget => widget.key !== 'street-mode');
        }

        return [
            {
                view: "2column",
                title: t(modalFormData.translationLabel),
                collapsed: false,
                key: "customer-new-address",
                widgets: mergedWidgetsWithStaticConfig,
            },
        ];
    }, [
        address,
        streetMode,
        settlementId,
        streetId,
        buildId,
        addressTypes,
        buildingNumbers,
        zipCodes,
        possibleAddress,
    ]);

    async function fetchAddressTypes() {
        setIsAddressTypesLoading(true);
        try {
            const response = await baseService.get("customer_address_type", {
                data: { objectType, objectId: customerId, src: inputKey },
            });
            if (response && response.success) {
                setAddressTypes(response.result);
            }
        } catch (e) {
            console.error("AddressForm::fetchAddressTypes: Error", e);
        } finally {
            setIsAddressTypesLoading(false);
        }
    }

    async function fetchBuildingNumbers(streetId) {
        setIsBuildingNumbersLoading(true);
        try {
            const response = await baseService.get("search_building", {
                data: { streetId, src: inputKey },
            });
            if (response && response.success) {
                setBuildingNumbers(response.result);
                return response.result;
            }
        } catch (e) {
            console.error("AddressForm::fetchBuildingNumbers: Error", e);
        } finally {
            setIsBuildingNumbersLoading(false);
        }
    }

    async function fetchZipCodes(streetId, buildId) {
        setIsZipCodesLoading(true);
        try {
            const response = await baseService.get("search_zip_code", {
                data: { streetId, buildId },
            });
            if (response && response.success && Array.isArray(response.result)) {
                if (possibleAddress) {
                    // check if current index exist in result
                    const currentZipCode = possibleAddress.zipCode;
                    const isAvailable = response.result.find(item => item.zip_code === currentZipCode);
                    // console.log({currentZipCode, isAvailable, zipCodes: response.result});
                    // if index by which we were searching the address is not inside of result - we should clear possible address and zipCode
                    if (!isAvailable) {
                        change(addressFormName, "zipCode", "");
                        setPossibleAddress(null);
                    }
                }
                setZipCodes(response.result);
                return response.result;
            }
        } catch (e) {
            console.error("AddressForm::fetchZipCodes: Error", e);
        } finally {
            setIsZipCodesLoading(false);
        }
    }

    async function fetchPossibleAddressByZipCode(zipCodeInput) {
        const regex = new RegExp(`^${ZIP_CODE_REGEX}$`);
        const isValid = regex.test(zipCodeInput);
        if (!isValid) {
            setPossibleAddress(null);
        }
        if (isValid && !isZipCodeAddressLoading) {
            setIsZipCodeAddressLoading(true);
            try {
                const response = await baseService.get("search_settlement_by_zip_code", {
                    data: { query: zipCodeInput },
                });
                if (response && response.success && Array.isArray(response.result) && response.result.length) {
                    setPossibleAddress({
                        ...response.result[0],
                        addressType,
                        buildingNumber: buildIdCreatableInput || buildId,
                        buildingSecondNumber,
                        apartmentNumber,
                        zipCode: zipCodeInput,
                        description,
                    });
                    const newStreetId = get(response, "result[0].street[0].id");
                    if (newStreetId) {
                        fetchBuildingNumbers(newStreetId);
                    }
                    setFormRerenderKey(new Date().getTime());
                    return response.result;
                }
            } catch (e) {
                console.error("AddressForm::fetchPossibleAddressByZipCode: Error", e);
            } finally {
                setIsZipCodeAddressLoading(false);
            }
        }
    }

    async function initAddressOptions() {
        const street = get(address, "street.id", "");
        const numbers = await fetchBuildingNumbers(street);
        const buildingNumber = get(address, "buildingNumber", "");
        const buildingNumberOption = numbers.find(item => item.name === buildingNumber);
        if (buildingNumberOption) {
            await fetchZipCodes(street, buildingNumberOption.id);
        }
        setIsOptionsInitialised(true);
    }

    function handleStreetMode(mode) {
        setStreetMode(mode);
        change(addressFormName, "buildingNumber", "");
        change(addressFormName, "buildingSecondNumber", "");
        change(addressFormName, "apartmentNumber", "");
        change(addressFormName, "zipCode", "");
        change(addressFormName, "streetMode", mode);
        // clear building number refs
        change(addressFormName, "buildingNumberRefPartyId", "");
        change(addressFormName, "buildingNumberLabelWithoutRef", "");


        setBuildingNumbers([]);
        setZipCodes([]);
    }

    function handleFieldChange(event, newValue, previousValue, name) {
        // handle fields reset here (in order to avoid reset fields on saved address edit init)
        if (name === "settlement") {
            change(addressFormName, "street", "");
            change(addressFormName, "buildingNumber", "");
            change(addressFormName, "buildingSecondNumber", "");
            change(addressFormName, "apartmentNumber", "");
            change(addressFormName, "zipCode", "");
            // clear building number refs
            change(addressFormName, "buildingNumberRefPartyId", "");
            change(addressFormName, "buildingNumberLabelWithoutRef", "");

            setBuildingNumbers([]);
            setZipCodes([]);
        }

        if (name === "street") {
            change(addressFormName, "buildingNumber", "");
            change(addressFormName, "buildingSecondNumber", "");
            change(addressFormName, "apartmentNumber", "");
            // clear building number refs
            change(addressFormName, "buildingNumberRefPartyId", "");
            change(addressFormName, "buildingNumberLabelWithoutRef", "");
            setBuildingNumbers([]);
            // we need to check if there was not a search by zipCode in order to reset it
            const isSearchedByZipCode = possibleAddress;
            if (!isSearchedByZipCode) {
                change(addressFormName, "zipCode", "");
                setZipCodes([]);
            }
            // fetch buildings options
            if (newValue) {
                fetchBuildingNumbers(newValue);
            }
        }

        if (name === "buildingNumber") {
            change(addressFormName, "buildingSecondNumber", "");
            change(addressFormName, "apartmentNumber", "");

            const selectedBuildingNumber = buildingNumbers.find(building => building.id === newValue?.value);

            // building number ref process logic
            // if (selectedBuildingNumber && selectedBuildingNumber.ref && selectedBuildingNumber.id) {
            if (selectedBuildingNumber && selectedBuildingNumber.ref && ![null, undefined].includes(selectedBuildingNumber.ref.id)) {
                change(addressFormName, "buildingNumberRefPartyId", selectedBuildingNumber.ref.id);
                change(addressFormName, "buildingNumberLabelWithoutRef", selectedBuildingNumber.name);
            } else {
                // clear building number refs
                change(addressFormName, "buildingNumberRefPartyId", "");
                change(addressFormName, "buildingNumberLabelWithoutRef", "");
            }

            // we need to check if there was not a search by zipCode in order to reset it
            const isSearchedByZipCode = possibleAddress;
            if (!isSearchedByZipCode) {
                change(addressFormName, "zipCode", "");
                setZipCodes([]);
            }
            // fetch zipCodes options
            const buildingNumberId = get(newValue, "value");
            if (buildingNumberId && streetMode === StreetModes.AUTO) {
                fetchZipCodes(streetId, buildingNumberId);
            }
        }

        if (name === "zipCode") {
            const newZipCode = newValue && newValue.value;
            if (possibleAddress && possibleAddress.zipCode && possibleAddress.zipCode !== newZipCode) {
                setPossibleAddress(null);
            }
        }
    }

    function retriggerValidation() {
        change(addressFormName, "_validationTriggerHack", Date.now());
    }

    useEffect(() => {
        if (onValuesUpdate && typeof onValuesUpdate === "function") {
            onValuesUpdate(formValues);
        }
    }, [formValues]);

    useEffect(() => {
        if (onErrorsUpdate && typeof onErrorsUpdate === "function") {
            onErrorsUpdate(formErrors);
        }
    }, [formErrors]);

    useEffect(() => {
        if (streetMode === StreetModes.AUTO && streetId) {
            fetchBuildingNumbers(streetId);
        }
        if (onStreetModeUpdate && typeof onStreetModeUpdate === "function") {
            onStreetModeUpdate(streetMode);
        }
        // change in order to trigger validation on street mode change
        if (!withoutStreetMode) {
            retriggerValidation();
        }
    }, [streetMode]);

    useEffect(() => {
        if (buildIdCreatableInput) {
            change(addressFormName, "buildingSecondNumber", "");
            change(addressFormName, "apartmentNumber", "");

            // we need to check if there was not a search by zipCode in order to reset it
            const isSearchedByZipCode = possibleAddress;
            if (!isSearchedByZipCode) {
                change(addressFormName, "zipCode", "");
                setZipCodes([]);
            }
        }
    }, [buildIdCreatableInput]);

    useEffect(() => {
        // no settlement selected - try to prefetch settlement and street (with street options) based on zipCode provided (if valid)
        if (!settlementId) {
            fetchPossibleAddressByZipCode(zipCodeCreatableInput);
        }
    }, [zipCodeCreatableInput]);

    useEffect(() => {
        if (onBuildingNumbersUpdate && typeof onBuildingNumbersUpdate === 'function') {
            onBuildingNumbersUpdate(buildingNumbers);
        }
    }, [buildingNumbers]);

    useEffect(() => {
        const shouldFetchTypes = !withoutAddressType && !address;
        // we should fetch available buildingNumbers / zipCodes options before editing address
        if (address) {
            setIsOptionsInitialised(false);
            initAddressOptions();
        }
        if (shouldFetchTypes) {
            fetchAddressTypes();
        } else {
            setIsAddressTypesLoading(false);
        }
    }, []);

    return (
        <>
            {(isAddressTypesLoading || !isOptionsInitialised || isZipCodeAddressLoading) && <Loader />}
            {!isAddressTypesLoading && isOptionsInitialised && (
                <DynamicForm
                    t={t}
                    formName={addressFormName}
                    dynData={initAddressForm}
                    objectId={customerId}
                    currentId={customerId}
                    objectType={objectType}
                    maxFileSize={maxFileSize}
                    onFieldChange={handleFieldChange}
                    withoutLiveSaver
                    key={formRerenderKey} // reinit form due to possible address change
                />
            )}
        </>
    );
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(AddressForm));
